import { useEffect, useRef } from 'react'
import { sourceBytesWithForms } from '@/lib/commands'
import { allSources } from '@/lib/pdf/sources'
import { makeCoverThumb } from '@/lib/pdf/thumbs'
import { replaceSourceBytes, saveSession } from '@/lib/storage'
import { useDoc } from '@/store/documentStore'

/**
 * Autosave the working state (pages + annotations) to IndexedDB shortly after every change,
 * so a closed tab, crash or phone reboot never loses work. Also registers the file in Recents.
 */
export function useAutosave() {
  const sessionId = useDoc((s) => s.sessionId)
  const revision = useDoc((s) => s.revision)
  const status = useDoc((s) => s.status)
  const lastForms = useRef(0)
  const thumbFor = useRef<string | null>(null)

  useEffect(() => {
    if (status !== 'ready' || !sessionId) return
    const t = setTimeout(
      async () => {
        const s = useDoc.getState()
        if (s.sessionId !== sessionId) return
        try {
          let thumb: string | undefined
          if (thumbFor.current !== sessionId || revision === 0) {
            thumbFor.current = sessionId
            thumb = s.pages[0] ? await makeCoverThumb(s.pages[0]).catch(() => undefined) : undefined
          }
          // persist filled-in form values by storing the updated primary document
          if (s.formRevision !== lastForms.current && s.sources[0]) {
            lastForms.current = s.formRevision
            const bytes = await sourceBytesWithForms(s.sources[0].id)
            await replaceSourceBytes(sessionId, s.sources[0].id, bytes)
          }
          const size = allSources().reduce((n, x) => n + x.bytes.byteLength, 0)
          await saveSession(
            {
              id: sessionId,
              name: s.fileName,
              sources: s.sources,
              pages: s.pages,
              annotations: s.annotations,
            },
            {
              pageCount: s.pages.length,
              size,
              thumb,
              edited: s.past.length > 0 || Object.values(s.annotations).some((l) => l.length > 0),
            },
          )
        } catch (e) {
          console.warn('autosave failed', e)
        }
      },
      revision === 0 ? 50 : 900,
    )
    return () => clearTimeout(t)
  }, [sessionId, revision, status])
}
